import { useMemo } from "react";
import {
  useGetDashboardStatsQuery,
  useOrganizationUsersQuery,
} from "../../../redux/api";
import type { NamedValue } from "../../../redux/api";
import { useAppSelector } from "../../../redux/store/hooks";
import { StatCard } from "../../../components/StatCard";
import {
  ChartCard,
  ColumnChart,
  DonutChart,
  StackedBar,
  BarList,
  BarChart,
  UnitGrid,
  useChartColors,
  SERIES_SLOTS,
  type LegendEntry,
} from "../../../components/charts";
import { IconBox, IconAlert, IconChart, IconForms } from "../../../components/icons";

// Ported from mobileapp's DashboardScreen — same single request, same derived
// figures, same palette. Shared by the owner/admin/manager homes.

const ROLE_ORDER = ["owner", "admin", "manager", "employee"] as const;
const ROLE_LABEL: Record<(typeof ROLE_ORDER)[number], string> = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  employee: "Employee",
};

const share = (value: number, total: number) =>
  total > 0 ? `${Math.round((value / total) * 100)}%` : "0%";

const sentenceCase = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const InventoryDashboard = () => {
  // Chart hues depend on the active theme (see charts/chartColors) — the dark
  // steps are different values, not the light ones dimmed.
  const chartColors = useChartColors();
  const organization = useAppSelector((s) => s.layout.organization);
  const orgId = organization?.id;

  const { data: stats, isLoading } = useGetDashboardStatsQuery(orgId ?? undefined);
  const { data: memberships = [], isLoading: loadingTeam } = useOrganizationUsersQuery(
    orgId ?? "",
    { skip: !orgId },
  );

  const currency = stats?.currency ?? "";
  const cash = (value: number) =>
    `${currency ? `${currency} ` : ""}${Math.round(value).toLocaleString()}`;

  const waiting = isLoading ? "Loading…" : "No data yet.";

  const team = useMemo(() => {
    const counts = new Map<string, number>();
    let other = 0;
    memberships.forEach((membership) => {
      const held = [...(membership.roles ?? [])].map((r) => r.toLowerCase());
      const highest = ROLE_ORDER.find((role) => held.includes(role));
      if (highest) counts.set(highest, (counts.get(highest) ?? 0) + 1);
      else other += 1;
    });

    // An org always has exactly one owner, but that owner is often absent from
    // organizationUsers() — an org created at signup records its owner in the
    // user record's embedded map instead. Without this floor the chart reports
    // "Owners 0" to the owner reading it.
    counts.set("owner", Math.max(1, counts.get("owner") ?? 0));

    const rows = ROLE_ORDER.map((role) => ({
      key: role as string,
      role: role as string | null,
      value: counts.get(role) ?? 0,
    }));
    const all = other > 0 ? [...rows, { key: "__other", role: null, value: other }] : rows;
    return { rows: all, total: all.reduce((sum, row) => sum + row.value, 0) };
  }, [memberships]);

  const teamRows = team.rows.map((row, index) => ({
    ...row,
    label: row.role ? ROLE_LABEL[row.role as (typeof ROLE_ORDER)[number]] : "Other",
    color: row.role ? chartColors.series(index) : chartColors.other,
  }));

  // Four categories keep their own hue; the rest sum into one real "Other".
  const withHues = (items: NamedValue[]) => {
    const head = items.slice(0, SERIES_SLOTS);
    const tail = items.slice(SERIES_SLOTS).reduce((sum, item) => sum + item.value, 0);
    return [
      ...head.map((item, index) => ({ ...item, color: chartColors.series(index) })),
      ...(tail > 0
        ? [{ key: "__other", label: "Other", value: tail, color: chartColors.other }]
        : []),
    ];
  };

  const months = stats?.monthly ?? [];
  const windowSales = months.reduce((sum, month) => sum + month.sales, 0);
  const windowPurchases = months.reduce((sum, month) => sum + month.purchases, 0);

  const health = stats?.stockHealth;
  const productCount = stats?.productCount ?? 0;

  const stockSlices = health
    ? [
        { key: "healthy", label: "In stock", value: health.healthy, color: chartColors.status.good },
        { key: "low", label: "Low stock", value: health.low, color: chartColors.status.warning },
        { key: "out", label: "Out of stock", value: health.out, color: chartColors.status.critical },
      ]
    : [];
  const stockLegend: LegendEntry[] = stockSlices.map((slice) => ({
    key: slice.key,
    label: slice.label,
    color: slice.color,
    value: `${slice.value} · ${share(slice.value, productCount)}`,
  }));

  // Coloured categorically, not by status tone: statusTone maps both `draft`
  // and `placed` to warning, which is useless in a composition bar.
  const orderStatus = withHues(stats?.orderStatus ?? []);
  const orderTotal = orderStatus.reduce((sum, slice) => sum + slice.value, 0);
  const categoryBars = withHues(stats?.categoryCount ?? []);

  return (
    <div className="dashboard">
      <div className="dashboard-banner">
        <div>
          <h1>Dashboard</h1>
          <p>{organization?.name ?? "An at-a-glance overview of your organization."}</p>
        </div>
      </div>

      <div className="stat-grid">
        <StatCard
          label="Products"
          value={isLoading ? "…" : productCount}
          tone="primary"
          icon={<IconBox />}
        />
        <StatCard
          label="Low stock"
          value={isLoading ? "…" : (stats?.lowStockCount ?? 0)}
          hint="At or below reorder level"
          tone="warning"
          icon={<IconAlert />}
        />
        <StatCard
          label="Inventory value"
          value={isLoading ? "…" : cash(stats?.inventoryValue ?? 0)}
          hint="Stock on hand at selling price"
          tone="success"
          icon={<IconChart />}
        />
        <StatCard
          label="Sales value"
          value={isLoading ? "…" : cash(stats?.salesValue ?? 0)}
          hint={`${stats?.saleCount ?? 0} sale(s)`}
          tone="primary"
          icon={<IconForms />}
        />
      </div>

      <h2 className="dashboard__section">Analytics</h2>

      <div className="chart-grid">
        <ChartCard
          title="Sales vs purchases"
          subtitle="Last six months"
          headline={cash(windowSales)}
          headlineCaption="Sales"
          legend={[
            { key: "sales", label: "Sales", color: chartColors.series(0), value: cash(windowSales) },
            {
              key: "purchases",
              label: "Purchases",
              color: chartColors.series(1),
              value: cash(windowPurchases),
            },
          ]}
          empty={windowSales + windowPurchases <= 0}
          emptyLabel={waiting}
        >
          <ColumnChart
            groups={months.map((month) => ({
              key: month.key,
              label: month.label,
              values: [month.sales, month.purchases],
            }))}
            series={[
              { key: "sales", label: "Sales", color: chartColors.series(0) },
              { key: "purchases", label: "Purchases", color: chartColors.series(1) },
            ]}
            formatValue={cash}
          />
        </ChartCard>

        <ChartCard
          title="Stock health"
          subtitle="Every product, in one of three states"
          legend={stockLegend}
          empty={productCount <= 0}
          emptyLabel={waiting}
        >
          <DonutChart
            slices={stockSlices}
            centerValue={String(productCount)}
            centerLabel="products"
          />
        </ChartCard>

        <ChartCard
          title="Orders by status"
          subtitle="Where your purchase orders stand"
          headline={String(orderTotal)}
          headlineCaption="Orders"
          legend={orderStatus.map((slice) => ({
            key: slice.key,
            label: sentenceCase(slice.label),
            color: slice.color,
            value: `${slice.value} · ${share(slice.value, orderTotal)}`,
          }))}
          empty={orderTotal <= 0}
          emptyLabel={waiting}
        >
          <StackedBar segments={orderStatus} />
        </ChartCard>

        <ChartCard
          title="Top products"
          subtitle="By sales value"
          empty={!stats?.topProducts.length}
          emptyLabel={waiting}
        >
          <BarList items={stats?.topProducts ?? []} formatValue={cash} />
        </ChartCard>

        <ChartCard
          title="Value by category"
          subtitle="Stock on hand at selling price"
          headline={cash(stats?.inventoryValue ?? 0)}
          headlineCaption="Inventory value"
          empty={!stats?.categoryValue.length}
          emptyLabel={waiting}
        >
          <BarList items={stats?.categoryValue ?? []} formatValue={cash} />
        </ChartCard>

        <ChartCard
          title="Products per category"
          subtitle="How the catalogue is spread"
          legend={categoryBars.map((bar) => ({
            key: bar.key,
            label: bar.label,
            color: bar.color,
            value: String(bar.value),
          }))}
          empty={!categoryBars.length}
          emptyLabel={waiting}
        >
          <BarChart items={categoryBars} />
        </ChartCard>

        <ChartCard
          title="Team"
          subtitle="One square per person"
          headline={String(team.total)}
          headlineCaption="people"
          legend={teamRows.map((row) => ({
            key: row.key,
            label: row.label,
            color: row.color,
            value: `${row.value} · ${share(row.value, team.total)}`,
          }))}
          empty={team.total <= 0}
          emptyLabel={loadingTeam ? "Loading…" : "No data yet."}
        >
          <UnitGrid groups={teamRows} />
        </ChartCard>
      </div>
    </div>
  );
};

export default InventoryDashboard;
